import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ProductDetail = () => {
  const { slug } = useParams();
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    quantity: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);

  const productData = {
    'makhana': {
      name: 'Makhana (Fox Nuts)',
      image: 'https://images.pexels.com/photos/4022090/pexels-photo-4022090.jpeg',
      description: 'Premium quality fox nuts (Euryale Ferox) sourced from the best farms in Bihar, India. Makhana is a highly nutritious snack and ingredient, rich in protein and low in fat.',
      variants: ['Grade A+', 'Grade A', 'Grade B', 'Mixed Grade'],
      qualityParams: [
        'Moisture Content: 8-10% max',
        'Size: 12mm-18mm diameter',
        'Purity: 99% minimum',
        'Broken: 2-5% max',
        'Color: Uniform white/cream'
      ],
      packaging: ['5kg, 10kg, 20kg poly bags', 'Custom packaging available', 'Export-grade cartons with liner'],
      exportCapability: 'Available for bulk export with APEDA certification support. Minimum order quantity varies by grade.'
    },
    'basmati-rice': {
      name: 'Basmati Rice',
      image: 'https://images.pexels.com/photos/2589457/pexels-photo-2589457.jpeg',
      description: 'Premium aromatic long-grain Basmati rice from the foothills of the Himalayas. Known for its distinctive fragrance, elongation upon cooking, and superior taste.',
      variants: ['1121 Basmati', 'Pusa Basmati', 'Traditional Basmati', 'Aged Basmati (1-2 years)'],
      qualityParams: [
        'Average grain length: 8.0-8.5mm',
        'Broken grains: 1-5% max',
        'Moisture content: 12-13% max',
        'Elongation ratio: 2x minimum',
        'Purity: 95% minimum'
      ],
      packaging: ['25kg, 40kg, 50kg PP bags', 'Custom retail packaging', 'Container stuffing with proper dunnage'],
      exportCapability: 'Fully export-ready with all required documentation. Suitable for Middle East, Europe, and North American markets.'
    },
    'non-basmati-rice': {
      name: 'Non-Basmati Rice',
      image: 'https://images.pexels.com/photos/7363671/pexels-photo-7363671.jpeg',
      description: 'High-quality non-basmati rice varieties ideal for daily consumption and industrial use. Sourced from prime rice-growing regions of India.',
      variants: ['Sona Masoori', 'IR64', 'Parboiled Rice', 'Broken Rice'],
      qualityParams: [
        'Grain length: 5.0-6.5mm',
        'Broken percentage: Varies by grade (5%-100%)',
        'Moisture: 13-14% max',
        'Foreign matter: 0.1% max',
        'Whiteness: 60-70 (for white rice)'
      ],
      packaging: ['25kg, 50kg PP bags', 'Bulk containers', 'Customized as per requirement'],
      exportCapability: 'Competitive pricing for bulk exports. Suitable for African and Asian markets.'
    },
    'dehydrated-onion-powder': {
      name: 'Dehydrated Onion Powder',
      image: 'https://images.pexels.com/photos/6157049/pexels-photo-6157049.jpeg',
      description: 'Industrial food-grade dehydrated onion powder manufactured using controlled dehydration processes. Retains natural flavor and aroma.',
      variants: ['Onion Powder', 'Onion Flakes', 'Onion Granules', 'Onion Minced'],
      qualityParams: [
        'Moisture content: 4-6% max',
        'Mesh size: 80-100 mesh (powder)',
        'Color: Creamy white to light yellow',
        'Pungency: Characteristic of fresh onion',
        'Microbial standards: As per HACCP'
      ],
      packaging: ['10kg, 15kg aluminum foil bags', '25kg poly-lined cartons', 'Nitrogen flushed packaging available'],
      exportCapability: 'Widely used in food processing industries globally. Export with complete quality certifications.'
    },
    'vegetables': {
      name: 'Fresh Vegetables',
      image: 'https://images.pexels.com/photos/1435904/pexels-photo-1435904.jpeg',
      description: 'Export-grade fresh vegetables sourced through our extensive farm network. Seasonal availability with proper cold chain management.',
      variants: ['Potatoes', 'Onions', 'Tomatoes', 'Green Chilies', 'Okra', 'Bitter Gourd', 'Seasonal Vegetables'],
      qualityParams: [
        'Freshness: Harvested within 24-48 hours',
        'Grading: Size and color uniformity',
        'No mechanical damage',
        'Free from pesticide residue (within permissible limits)',
        'Pre-cooling done before packing'
      ],
      packaging: ['Ventilated cartons', 'Net bags', 'Custom packaging as per market standards', 'Temperature-controlled containers'],
      exportCapability: 'Cold storage and reefer container support available. Suitable for Middle East and Southeast Asian markets.'
    },
    'fresh-fruits': {
      name: 'Fresh Fruits',
      image: 'https://images.pexels.com/photos/1132047/pexels-photo-1132047.jpeg',
      description: 'Premium quality fresh fruits with export-grade sorting and packaging. Seasonal availability based on harvest cycles.',
      variants: ['Mangoes (Alphonso, Kesar, Totapuri)', 'Pomegranates', 'Grapes', 'Bananas', 'Oranges', 'Apples'],
      qualityParams: [
        'Ripeness: As per destination requirements',
        'Brix level: Measured for sweetness',
        'Size grading: Uniform sizing',
        'Surface quality: No blemishes or damage',
        'Shelf life optimization'
      ],
      packaging: ['Telescopic cartons', 'Corrugated boxes with padding', 'Individual fruit wrapping', 'Reefer containers'],
      exportCapability: 'Experience in exporting to EU, Middle East, and Asia with proper phytosanitary compliance.'
    },
    'spices': {
      name: 'Spices',
      image: 'https://images.pexels.com/photos/4198894/pexels-photo-4198894.jpeg',
      description: 'Authentic Indian spices in whole and powdered forms. Sourced from specific regions known for best quality in each spice category.',
      variants: ['Turmeric', 'Red Chili', 'Coriander', 'Cumin', 'Black Pepper', 'Cardamom', 'Cloves', 'Cinnamon'],
      qualityParams: [
        'Purity: 98-99% minimum',
        'Moisture: 8-12% (varies by spice)',
        'Color value: As per variety standards',
        'Volatile oil content: Within specified range',
        'Lab testing: Heavy metals, aflatoxin, pesticides'
      ],
      packaging: ['PP bags (25kg, 50kg)', 'Aluminum foil pouches', 'Multi-layer BOPP bags', 'Vacuum packaging available'],
      exportCapability: 'Full compliance with importing country standards. Spice board certification support.'
    },
    'pulses': {
      name: 'Pulses',
      image: 'https://images.pexels.com/photos/4750274/pexels-photo-4750274.jpeg',
      description: 'Cleaned and graded pulses for bulk supply. Machine-cleaned with sorting to ensure consistent quality.',
      variants: ['Chickpeas (Kabuli & Desi)', 'Lentils (Masoor)', 'Pigeon Peas (Toor)', 'Mung Beans', 'Black Gram (Urad)'],
      qualityParams: [
        'Cleaning: Machine-cleaned and sorted',
        'Moisture: 10-12% max',
        'Foreign matter: 0.5% max',
        'Damaged/discolored: 2-3% max',
        'Uniform size and color'
      ],
      packaging: ['25kg, 50kg PP bags', 'Jute bags', 'Custom packaging', 'Container loading with proper stacking'],
      exportCapability: 'Competitive bulk supply for international markets. Popular in Middle East, Africa, and Asia.'
    },
    'coffee-beans': {
      name: 'Coffee Beans',
      image: 'https://images.pexels.com/photos/894695/pexels-photo-894695.jpeg',
      description: 'Premium Arabica and Robusta coffee beans from the coffee estates of South India. Available in green bean and roasted forms.',
      variants: ['Arabica (Plantation, Washed)', 'Robusta (Cherry, Parchment)', 'Roasted Beans', 'Custom Blends'],
      qualityParams: [
        'Bean size: AA, A, B, C grades',
        'Moisture: 11-12% for green beans',
        'Defects: Graded as per ICO standards',
        'Aroma and flavor profile testing',
        'Cup quality: 80+ score for specialty grades'
      ],
      packaging: ['60kg jute bags (green beans)', 'Vacuum-sealed bags (roasted)', 'GrainPro bags for specialty coffee', 'Export-grade containers'],
      exportCapability: 'Experience in specialty and commercial grade exports. Coffee Board certification available.'
    },
    'tea': {
      name: 'Tea',
      image: 'https://images.pexels.com/photos/1638280/pexels-photo-1638280.jpeg',
      description: 'Premium quality tea from the renowned tea gardens of Assam, Darjeeling, and Nilgiris. CTC and orthodox varieties available.',
      variants: ['CTC Black Tea', 'Orthodox Black Tea', 'Green Tea', 'Darjeeling Tea', 'Assam Tea'],
      qualityParams: [
        'Moisture: 3-5% max',
        'Leaf grade: FTGFOP, SFTGFOP, BOP, etc.',
        'Liquor color and brightness',
        'Aroma and flavor profile',
        'Tannin and caffeine content'
      ],
      packaging: ['Aluminum-lined multi-wall paper sacks', 'Tea chests (plywood)', 'Bulk bags', 'Retail packaging with nitrogen flushing'],
      exportCapability: 'Traditional tea exporter with knowledge of regional preferences. Tea Board registration support.'
    },
  };

  const product = productData[slug] || productData['makhana'];

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.name || !formData.email || !formData.quantity) {
      toast.error('Please fill in all required fields');
      return;
    }
    
    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      toast.error('Please enter a valid email address');
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/product-inquiry`, {
        ...formData,
        product_name: product.name
      });
      toast.success('Inquiry submitted successfully! We will contact you soon.');
      setFormData({ name: '', email: '', phone: '', quantity: '', message: '' });
    } catch (error) {
      console.error('Error submitting inquiry:', error);
      toast.error('Failed to submit inquiry. Please try again or contact us via WhatsApp.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Breadcrumb */}
      <div className="bg-[#F8FAFC] py-6">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <Link to="/products" className="inline-flex items-center gap-2 text-[#0B1B32] hover:gap-3 transition-all" data-testid="back-to-products">
            <ArrowLeft size={18} /> Back to Products
          </Link>
        </div>
      </div>

      {/* Product Header */}
      <section className="py-16 md:py-24">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <img src={product.image} alt={product.name} className="w-full h-[500px] object-cover shadow-2xl" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl font-medium tracking-tight text-[#0B1B32] mb-6" data-testid="product-title">
                {product.name}
              </h1>
              <p className="text-slate-600 leading-relaxed text-lg mb-6">
                {product.description}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Product Details */}
      <section className="py-16 bg-[#F8FAFC]">
        <div className="container mx-auto px-6 md:px-12 max-w-7xl">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            {/* Variants */}
            <div>
              <h2 className="text-2xl font-medium text-[#0B1B32] mb-4">Available Variants</h2>
              <ul className="space-y-2">
                {product.variants.map((variant, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{variant}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Quality Parameters */}
            <div>
              <h2 className="text-2xl font-medium text-[#0B1B32] mb-4">Quality Parameters</h2>
              <ul className="space-y-2">
                {product.qualityParams.map((param, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{param}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Packaging */}
            <div>
              <h2 className="text-2xl font-medium text-[#0B1B32] mb-4">Packaging Options</h2>
              <ul className="space-y-2">
                {product.packaging.map((pack, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-600">{pack}</span>
                  </li>
                ))}
              </ul>
            </div>

            {/* Export Capability */}
            <div>
              <h2 className="text-2xl font-medium text-[#0B1B32] mb-4">Export Capability</h2>
              <p className="text-slate-600 leading-relaxed">{product.exportCapability}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="py-16 md:py-24 bg-white">
        <div className="container mx-auto px-6 md:px-12 max-w-3xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-medium tracking-tight text-[#0B1B32] mb-4">
              Request a Quote
            </h2>
            <p className="text-slate-600">
              Fill in your details and we'll get back to you with pricing and availability
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6" data-testid="inquiry-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Name *</label>
                <Input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  data-testid="inquiry-name"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Email *</label>
                <Input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  data-testid="inquiry-email"
                  className="w-full"
                />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Phone</label>
                <Input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  data-testid="inquiry-phone"
                  className="w-full"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-2">Quantity Required *</label>
                <Input
                  type="text"
                  name="quantity"
                  placeholder="e.g., 10 tons"
                  value={formData.quantity}
                  onChange={handleChange}
                  required
                  data-testid="inquiry-quantity"
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">Additional Details</label>
              <Textarea
                name="message"
                rows={5}
                value={formData.message}
                onChange={handleChange}
                data-testid="inquiry-message"
                className="w-full"
                placeholder="Any specific requirements or questions..."
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              data-testid="submit-inquiry-button"
              className="w-full bg-[#0B1B32] text-white hover:bg-[#1e293b] rounded-none px-8 py-6 font-medium transition-all duration-300 text-sm tracking-wide shadow-lg hover:shadow-xl"
            >
              {loading ? 'Submitting...' : 'Submit Inquiry'}
            </Button>
          </form>
        </div>
      </section>
    </div>
  );
};

export default ProductDetail;